import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaSignOutAlt, FaTimes, FaChevronRight, FaChevronLeft, FaEllipsisV } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useSite } from '../../contexts/SiteContext.jsx';
import LanguageSelector from '../ui/LanguageSelector.jsx';
import { assets } from '../../assets/assets';

// Composant Skeleton pour les éléments du sidebar
const SkeletonSidebarElement = ({ className = '', width = '100%', height = '1rem' }) => {
  return (
    <div
      className={`bg-gray-200 rounded-lg ${className}`}
      style={{ 
        width, 
        height,
        background: 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%)',
        backgroundSize: '1000px 100%',
        animation: 'shimmer 2s infinite'
      }}
    />
  );
};

// Skeleton pour le Sidebar
const SkeletonSidebar = ({ isCollapsed, isDark }) => {
  return (
    <>
      {/* Brand Skeleton */}
      <div className={`flex items-center h-16 ${isCollapsed ? 'lg:px-2 px-6' : 'px-6'} border-b ${isDark ? 'border-gray-700 bg-gray-800/95' : 'border-gray-200 bg-white/95'}`}>
        <div className="flex items-center justify-between w-full">
          {!isCollapsed && (
            <>
              {/* Logo Skeleton */}
              <SkeletonSidebarElement height="4rem" width="11rem" className="rounded-lg" />
              {/* Bouton Skeleton */}
              <SkeletonSidebarElement height="2.25rem" width="2.25rem" className="rounded-full" />
            </>
          )}
          {isCollapsed && (
            <SkeletonSidebarElement height="2.25rem" width="2.25rem" className="rounded-full mx-auto" />
          )}
        </div>
      </div>

      {/* Menu Skeleton */}
      <nav className={`flex-1 py-6 overflow-y-auto ${isCollapsed ? 'lg:px-2 px-4' : 'px-4'}`}>
        <div className="mb-6">
          {!isCollapsed && (
            <SkeletonSidebarElement height="0.75rem" width="3rem" className="mb-3 px-3" />
          )}
          <div className="space-y-1">
            {[...Array(7)].map((_, i) => (
              <div
                key={i}
                className={`flex items-center ${isCollapsed ? 'lg:justify-center lg:px-2 px-3' : 'px-3'} py-3 rounded-xl`}
              >
                {/* Icône Skeleton */}
                <SkeletonSidebarElement height="2rem" width="2rem" className="rounded-lg" />
                {!isCollapsed && (
                  <>
                    <div className="ml-3 flex-1">
                      <SkeletonSidebarElement height="1rem" width="80%" className="rounded" />
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </nav>

      {/* Footer Skeleton */}
      <div className={`${isCollapsed ? 'lg:px-2 px-4' : 'px-4'} py-4 border-t space-y-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        {/* Site Selector Skeleton */}
        <div className="space-y-2">
          <SkeletonSidebarElement height="2.5rem" width="100%" className="rounded-xl" />
        </div>
        {/* Langue Skeleton */}
        <SkeletonSidebarElement height="2.5rem" width="100%" className="rounded-xl" />
        {/* Déconnexion Skeleton */}
        <SkeletonSidebarElement height="2.5rem" width="100%" className="rounded-xl" />
      </div>

      {/* Styles pour l'animation shimmer */}
      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }
      `}</style>
    </>
  );
};

const SidebarAdminRH = ({ isOpen, onClose, isCollapsed, onToggleCollapse, siteChanging = false }) => {
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [showSiteMenu, setShowSiteMenu] = useState(false);
  const siteMenuRef = useRef(null);
  const { handleLogout, user } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const {
    selectedSite,
    sites,
    multiSites,
    pointageInterSiteEnabled,
    pointageTerrainEnabled,
    paieEnabled,
    comptabilitePaieEnabled,
    selectSite,
    hasSiteManagement
  } = useSite();

  const toggleDropdown = (menuId) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Fermer le menu de site quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (siteMenuRef.current && !siteMenuRef.current.contains(event.target)) {
        setShowSiteMenu(false);
      }
    };

    if (showSiteMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSiteMenu]);

  // Vérifier si l'utilisateur a seulement la permission "Gestion Site"
  const userPermissions = user?.permissions || {};
  const hasOnlySiteManagement = userPermissions.can_manage_site && 
    !userPermissions.can_manage_employees &&
    !userPermissions.can_manage_departments &&
    !userPermissions.can_manage_schedules &&
    !userPermissions.can_manage_attendance &&
    !userPermissions.can_manage_leave &&
    !userPermissions.can_view_payroll &&
    !userPermissions.can_manage_payroll &&
    !userPermissions.can_validate_payroll &&
    !userPermissions.can_manage_payroll_accounting &&
    !userPermissions.can_invite_adminrh_plus;

  const canAccessPayrollModule =
    user?.role !== 'AdminRH+1' ||
    userPermissions.can_view_payroll ||
    userPermissions.can_manage_payroll ||
    userPermissions.can_validate_payroll ||
    userPermissions.can_manage_payroll_accounting;

  // Menu items spécifiques pour AdminRH
  const allMenuItems = [
    { id: 'dashboard', label: t('Dashboard'), icon: assets.home, path: '/dashboard' },
    { id: 'ressources', label: t('resources.menu', 'Ressources'), icon: assets.employes, path: '/ressources' },
    { id: 'admin-rh-plus', label: t('navigation.adminRHPlus'), icon: assets.employes, path: '/admin-rh-plus' },
    { id: 'horaires', label: t('Horaires'), icon: assets.retards, path: '/horaires' },
    { id: 'pointages', label: t('Pointages'), icon: assets.pointages, path: '/pointages' },
    ...(pointageTerrainEnabled
      ? [
          {
            id: 'pointage-terrain',
            label: t('fieldVisit.sidebarLabelTerrain'),
            icon: assets.pointages,
            path: '/pointage-terrain-admin'
          }
        ]
      : []),
    ...(multiSites && pointageInterSiteEnabled
      ? [
          {
            id: 'pointage-inter-site-admin',
            label: t('fieldVisit.sidebarLabelInterSite'),
            icon: assets.pointageInterSite,
            path: '/pointage-inter-site-admin'
          }
        ]
      : []),
    { id: 'conges', label: t('Conges'), icon: assets.presences, path: '/conges' },
    ...(paieEnabled
      && canAccessPayrollModule
      ? [
          { id: 'paie', label: 'Paie', icon: assets.rapport || assets.presences, path: '/paie' }
        ]
      : []),
    ...(paieEnabled && comptabilitePaieEnabled
      && canAccessPayrollModule
      ? [
          { id: 'comptabilite-paie', label: 'Comptabilité paie', icon: assets.parametres || assets.rapport, path: '/comptabilite-paie' }
        ]
      : []),
    { id: 'parametres', label: t('Parametres'), icon: assets.parametres, path: '/parametres' },
  ];

  // Filtrer le menu « gestion site uniquement » : garder Paramètres + modules pointage ext. si activés pour l’entreprise
  const menuItems = hasOnlySiteManagement
    ? allMenuItems.filter(
        (item) =>
          item.id === 'parametres' ||
          (item.id === 'paie' && paieEnabled) ||
          (item.id === 'comptabilite-paie' && paieEnabled && comptabilitePaieEnabled) ||
          (item.id === 'pointage-terrain' && pointageTerrainEnabled) ||
          (item.id === 'pointage-inter-site-admin' && multiSites && pointageInterSiteEnabled)
      )
    : allMenuItems;

  return (
    <>
      {/* Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 z-50 shadow-md transform transition-all duration-300 ease-in-out flex flex-col backdrop-blur-sm ${
          isCollapsed ? 'lg:w-20 w-65' : 'w-65'
        } ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${isDark ? 'bg-gray-800/95 border-r border-gray-700' : 'bg-white/95 border-r border-gray-200'}`}
      >
        {/* Afficher le skeleton si siteChanging est true */}
        {siteChanging ? (
          <SkeletonSidebar isCollapsed={isCollapsed} isDark={isDark} />
        ) : (
          <>
            {/* Brand */}
            <div className={`flex items-center h-16 ${isCollapsed ? 'lg:px-2 px-6' : 'px-6'} border-b ${isDark ? 'border-gray-700 bg-gray-800/95' : 'border-gray-200 bg-white/95'}`}>
          <div className="flex items-center justify-between w-full">
            {/* Logo quand sidebar est ouvert - poussé vers la gauche */}
            {!isCollapsed && (
              <>
                <img 
                  src={assets.secura_logo} 
                  alt="Secura" 
                  className="h-32 w-44 object-contain -ml-6"
                />
                {/* Bouton arrow à droite du logo */}
                <button
                  onClick={onToggleCollapse}
                  className="w-9 h-9 rounded-full bg-[#0026E2] flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:bg-[#0019B3] cursor-pointer"
                  title="Fermer le menu"
                >
                  <FaChevronLeft className="text-base text-white" />
                </button>
              </>
            )}
            {/* Bouton arrow centré quand sidebar est fermé sur desktop */}
            {isCollapsed && (
              <button
                onClick={onToggleCollapse}
                className="hidden lg:flex w-9 h-9 rounded-full bg-[#0026E2] items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg hover:bg-[#0019B3] cursor-pointer mx-auto"
                title="Ouvrir le menu"
              >
                <FaChevronRight className="text-base text-white" />
              </button>
            )}
            {/* Logo pour mobile */}
            {isCollapsed && (
              <img 
                src={assets.secura_logo} 
                alt="Secura" 
                className="lg:hidden h-32 w-44 object-contain"
              />
            )}
          </div>
          <button
            onClick={onClose}
            className={`lg:hidden ml-auto ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <FaTimes />
          </button>
        </div>

        {/* Menu */}
        <nav className={`flex-1 py-6 overflow-y-auto ${isCollapsed ? 'lg:px-2 px-4' : 'px-4'}`}>
          <div className="mb-6">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 px-3 ${isDark ? 'text-gray-400' : 'text-gray-500'} ${isCollapsed ? 'lg:hidden' : ''}`}>{t('common.menu', 'MENU')}</h3>
            <div className="space-y-1">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                
                return (
                  <Link
                    key={item.id}
                    to={item.path}
                    className={`flex items-center ${isCollapsed ? 'lg:justify-center lg:px-2 px-3' : 'px-3'} py-3 rounded-xl text-sm font-medium transition-all duration-300 group ${
                      isActive 
                        ? isDark 
                          ? 'bg-gradient-to-r from-blue-600/30 to-blue-700/30 text-blue-400 shadow-md border border-blue-500/30' 
                          : 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 shadow-sm border border-blue-200'
                        : isDark
                          ? 'text-gray-300 hover:bg-gradient-to-r hover:from-gray-700/50 hover:to-gray-600/50 hover:text-white hover:shadow-sm'
                          : 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 hover:text-gray-900 hover:shadow-sm'
                    }`}
                    title={isCollapsed ? item.label : ''}
                  >
                    <img
                      src={item.icon}
                      alt={item.label}
                      className={`${isCollapsed ? 'lg:mr-0 mr-3' : 'mr-3'} object-contain ${item.id === 'parametres' ? 'h-10 w-10' : 'h-8 w-8'}`}
                    />
                    <span className={`flex-1 ${isCollapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </nav>

        {/* Footer - Site, Langue et Déconnexion */}
        <div className={`${isCollapsed ? 'lg:px-2 px-4' : 'px-4'} py-4 border-t space-y-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          {/* Sélecteur de Site (si multi-sites activé et utilisateur n'a pas de site assigné) */}
          {multiSites && !hasOnlySiteManagement && !hasSiteManagement && (
            <div className={`relative ${isCollapsed ? 'lg:hidden' : ''}`} ref={siteMenuRef}>
              <div className="flex items-center gap-2">
                <button 
                  className="px-8 py-4 rounded-2xl font-semibold text-sm hover:opacity-90 transition-all duration-300 hover:scale-105 border border-gray-300 text-gray-700 hover:bg-gray-50 cursor-pointer flex-1"
                  onClick={() => setShowSiteMenu(!showSiteMenu)}
                >
                  {selectedSite?.nom || 'Sélectionner un site'}
                </button>
                <div className="relative group">
                  <div className="rounded-2xl p-1 flex items-center gap-1" style={{ backgroundColor: '#0026E2' }}>
                    <button
                      onClick={() => setShowSiteMenu(!showSiteMenu)}
                      className="p-2 hover:opacity-80 rounded transition-colors"
                      title="Effectuer une action"
                    >
                      <FaEllipsisV className="text-white" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Menu déroulant des sites */}
              {showSiteMenu && !isCollapsed && (
                <div className="absolute top-1/2 left-full ml-2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50 max-h-64 overflow-y-auto p-2 space-y-2 min-w-[220px]">
                  {sites.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      Aucun site configuré
                    </div>
                  ) : (
                    sites.map((site) => {
                      const isSelected = site.id === 'generalized' || site.isGeneralized
                        ? (selectedSite?.id === 'generalized' || selectedSite?.isGeneralized)
                        : selectedSite?.id === site.id;
                      
                      const isDefault = site.est_site_par_defaut && !site.isGeneralized;
                      
                      return (
                        <button
                          key={site.id || site.isGeneralized ? 'generalized' : site.id}
                          onClick={() => {
                            selectSite(site);
                            setShowSiteMenu(false);
                          }}
                          className={`w-full px-8 py-4 rounded-2xl font-semibold text-sm hover:opacity-90 transition-all duration-300 hover:scale-105 border ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100'
                              : isDefault
                              ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          } cursor-pointer flex items-center`}
                        >
                          <div className="flex-1 text-left">
                            <div className="font-semibold">{site.nom}</div>
                            {isDefault && (
                              <div className="text-xs text-gray-500 font-normal mt-0.5">Par défaut</div>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}

          {/* Sélecteur de Langue */}
          <div className={isCollapsed ? 'lg:hidden' : ''}>
            <LanguageSelector dropUp={true} />
          </div>
          
          {/* Bouton Déconnexion */}
          <button
            onClick={handleLogout}
            className={`w-full flex items-center ${isCollapsed ? 'lg:justify-center lg:px-2 px-3' : 'px-3'} py-3 rounded-xl text-sm font-medium transition-all duration-200 group ${
              isDark 
                ? 'text-white hover:bg-red-900/30' 
                : 'text-red-600 hover:bg-red-50 hover:text-red-700'
            }`}
            title={isCollapsed ? t('menu.logout', 'Déconnexion') : ''}
          >
            <FaSignOutAlt className={`${isCollapsed ? 'lg:mr-0 mr-3' : 'mr-3'} text-base group-hover:scale-110 transition-transform`} />
            <span className={`flex-1 text-left ${isCollapsed ? 'lg:hidden' : ''}`}>{t('menu.logout', 'Déconnexion')}</span>
          </button>
        </div>
          </>
        )}
      </div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-20 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}
    </>
  );
};

export default SidebarAdminRH;
